import React, { useState, useEffect, useRef } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Badge, Table, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, 
    Form, FormGroup, Label, Input, Button,
    Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import { APIClient } from '../../helpers/api_helper';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import Swal from 'react-bootstrap-sweetalert';


const SubscriptionPlans = () => {
    document.title = "Subscription Plans";
    
    const [plans, setPlans] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [addModal, setAddModal] = useState(false);
    const [newPlan, setNewPlan] = useState({
        planName: { en: '' },
        description: { en: '' },
        planSection: 'ESSENTIAL',
        amount: 0,
        currency: { currencyCode: 'USD', symbol: '$' },
        duration: 1,
        durationType: 'MONTH',
        appStoreId: '',
        playStoreId: '',
        adsVisibility: { value: 'Visible' },
        noOfTokensIncluded: { value: 5 },
        myList: {
            recentVisitorsValue: false,
            recentVisitorsDescription: { en: '' },
            myMatchesValue: false,
            myMatchesDescription: { en: '' },
            maybeValue: false,
            maybeDescription: { en: '' },
            myLikesValue: false,
            myLikesDescription: { en: '' },
            likesMeValue: false,
            likesMeDescription: { en: '' },
            likedHooksValue: false,
            likedHooksDescription: { en: '' }
        },
        allowUnlimitedRewinds: { 
            value: 'Enabled',
            description: { en: '' }
        },
        passportToAnotherLocation: { 
            value: 'Enabled',
            description: { en: '' }
        },
        advanceFilterAccess: { 
            value: false,
            description: { en: '' }
        },
        controlWhoSeesYourProfile: { 
            value: false,
            description: { en: '' }
        },
        aiMatches: { 
            value: false,
            description: { en: '' }
        },
        dailyLikeCount: { value: 5 }
    });
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const [activeTab, setActiveTab] = useState('active');

    // Group plans by section instead of name, handling case sensitivity
    const groupedPlans = plans.reduce((acc, plan) => {
        // Get section and normalize it for grouping
        let section = plan.planSection || 'Other';
        
        // Normalize to uppercase for consistent grouping
        const normalizedSection = section.toUpperCase();
        
        // Keep track of the original section name for display
        const group = acc[normalizedSection] || [];
        return { ...acc, [normalizedSection]: [...group, plan] };
    }, {});

    const fetchData = async () => {
        try {
            setLoading(true);
            const authData = JSON.parse(sessionStorage.getItem("authUser"));
            const token = authData?.accessToken || '';

            const [plansRes, currenciesRes] = await Promise.all([
                APIClient.get(`/subscriptionPlan/admin?status=${activeTab.toUpperCase()}${searchText ? `&searchText=${searchText}` : ''}`),
                APIClient.get('/currencies')
            ]);
            
            if (plansRes.data?.data) {
                setPlans(plansRes.data.data);
            }
            if (currenciesRes.data?.data) {
                setCurrencies(currenciesRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch subscription data', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchPlanDetails = async (planId) => {
        try {
            setLoading(true);
            const response = await APIClient.get(`/subscriptionPlan/admin?planId=${planId}&status=ACTIVE`);
            if (response.data?.data) {
                setEditForm(response.data.data);
                setEditModal(true);
                //toast.success('Plan details loaded successfully');
            }
        } catch (error) {
            console.error('Error fetching plan details:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch plan details', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (path, value) => {
        setEditForm(prev => {
            const newForm = { ...prev };
            const parts = path.split('.');
            let current = newForm;
            
            for (let i = 0; i < parts.length - 1; i++) {
                current = current[parts[i]];
            }
            current[parts[parts.length - 1]] = value;
            
            return newForm;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            // Ensure all required objects and properties exist
            const planName = editForm.planName || { en: '' };
            const description = editForm.description || { en: '' };
            const myList = editForm.myList || {};
            
            // Create objects with proper structure if they don't exist
            if (!editForm.noOfTokensIncluded) editForm.noOfTokensIncluded = { value: 0, description: { en: '' } };
            if (!editForm.dailyLikeCount) editForm.dailyLikeCount = { value: 0, description: { en: '' } };
            if (!editForm.allowUnlimitedRewinds) editForm.allowUnlimitedRewinds = { value: 'Disabled', description: { en: '' } };
            if (!editForm.passportToAnotherLocation) editForm.passportToAnotherLocation = { value: 'Disabled', description: { en: '' } };
            if (!editForm.advanceFilterAccess) editForm.advanceFilterAccess = { value: false, description: { en: '' } };
            if (!editForm.controlWhoSeesYourProfile) editForm.controlWhoSeesYourProfile = { value: false, description: { en: '' } };
            if (!editForm.adsVisibility) editForm.adsVisibility = { value: 'Visible', description: { en: '' } };
            if (!editForm.aiMatches) editForm.aiMatches = { value: false, description: { en: '' } };

            // Format planSection correctly - capitalize first letter only (Essential, Premium, Black, Other)
            let planSection = editForm.planSection || 'ESSENTIAL';
            if (planSection) {
                planSection = planSection.toLowerCase();
                planSection = planSection.charAt(0).toUpperCase() + planSection.slice(1);
            }

            const payload = {
                planName: { en: planName.en || '' },
                description: { en: description.en || '' },
                planSection: planSection,
                amount: editForm.amount || 0,
                currency: editForm.currency || { currencyCode: 'USD', symbol: '$' },
                duration: editForm.duration || 1,
                durationType: editForm.durationType || 'MONTH',
                appStoreId: editForm.appStoreId || '',
                playStoreId: editForm.playStoreId || '',
                adsVisibility: editForm.adsVisibility,
                noOfTokensIncluded: editForm.noOfTokensIncluded,
                myList: myList,
                allowUnlimitedRewinds: editForm.allowUnlimitedRewinds,
                passportToAnotherLocation: editForm.passportToAnotherLocation,
                advanceFilterAccess: editForm.advanceFilterAccess,
                controlWhoSeesYourProfile: editForm.controlWhoSeesYourProfile,
                aiMatches: editForm.aiMatches,
                dailyLikeCount: editForm.dailyLikeCount,
                planId: editForm._id
            };

            console.log('Sending payload:', payload);

            const response = await APIClient.patch('/subscriptionPlan', payload);
            
            if (response.data?.message) {
                toast.success(response.data.message, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
            }
            setEditModal(false);
            fetchData();
        } catch (error) {
            console.error('Error updating plan:', error);
            toast.error(error.response?.data?.message || 'Failed to update plan', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInactivate = (planId) => {
        setPlanToDelete(planId);
        setShowDeleteAlert(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await fetch('https://dev-api.koottutheapp.com/v1/subscriptionPlan', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || ''
                },
                body: JSON.stringify({
                    status: "INACTIVE",
                    planIds: [planToDelete]
                })
            });

            const data = await response.json();
            
            if (data.message) {
                toast.success(data.message, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                fetchData();
            }
        } catch (error) {
            console.error('Error inactivating plan:', error);
            toast.error(error.message || 'Failed to inactivate plan', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
        setShowDeleteAlert(false);
    };

    const handleAddPlan = async (e) => {
        e.preventDefault();
        try {
            // Format planSection correctly - capitalize first letter only (Essential, Premium, Black, Other)
            let planSection = newPlan.planSection || 'ESSENTIAL';
            if (planSection) {
                planSection = planSection.toLowerCase();
                planSection = planSection.charAt(0).toUpperCase() + planSection.slice(1);
            }
            
            // Create the payload with the correct structure
            const payload = {
                planName: { en: newPlan.planName.en },
                description: { en: newPlan.description.en },
                planSection: planSection,
                amount: newPlan.amount,
                currency: newPlan.currency,
                duration: newPlan.duration,
                durationType: newPlan.durationType,
                appStoreId: newPlan.appStoreId,
                playStoreId: newPlan.playStoreId,
                adsVisibility: {
                    value: newPlan.adsVisibility.value,
                    description: { en: newPlan.adsVisibility.description?.en || '' }
                },
                noOfTokensIncluded: {
                    value: newPlan.noOfTokensIncluded.value,
                    description: { en: newPlan.noOfTokensIncluded.description?.en || '' }
                },
                myList: {
                    recentVisitorsValue: newPlan.myList.recentVisitorsValue,
                    recentVisitorsDescription: { en: newPlan.myList.recentVisitorsDescription?.en || '' },
                    myMatchesValue: newPlan.myList.myMatchesValue,
                    myMatchesDescription: { en: newPlan.myList.myMatchesDescription?.en || '' },
                    maybeValue: newPlan.myList.maybeValue,
                    maybeValueDescription: { en: newPlan.myList.maybeDescription?.en || '' },
                    myLikesValue: newPlan.myList.myLikesValue,
                    myLikesDescription: { en: newPlan.myList.myLikesDescription?.en || '' },
                    likesMeValue: newPlan.myList.likesMeValue,
                    likesMeDescription: { en: newPlan.myList.likesMeDescription?.en || '' },
                    likedHooksValue: newPlan.myList.likedHooksValue,
                    likedHooksValueDescription: { en: newPlan.myList.likedHooksDescription?.en || '' }
                },
                allowUnlimitedRewinds: {
                    value: newPlan.allowUnlimitedRewinds.value,
                    description: { en: newPlan.allowUnlimitedRewinds.description?.en || '' }
                },
                passportToAnotherLocation: {
                    value: newPlan.passportToAnotherLocation.value,
                    description: { en: newPlan.passportToAnotherLocation.description?.en || '' }
                },
                advanceFilterAccess: {
                    value: newPlan.advanceFilterAccess.value
                },
                controlWhoSeesYourProfile: {
                    value: newPlan.controlWhoSeesYourProfile.value
                },
                aiMatches: {
                    value: newPlan.aiMatches.value,
                    description: { en: newPlan.aiMatches.description?.en || '' }
                },
                dailyLikeCount: {
                    value: newPlan.dailyLikeCount.value,
                    description: { en: newPlan.dailyLikeCount.description?.en || '' }
                }
            };

            const response = await fetch('https://dev-api.koottutheapp.com/v1/subscriptionPlan', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || ''
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            
            if (data.message) {
                toast.success(data.message, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
            }
        } catch (error) {
            console.error('Error adding plan:', error);
            toast.error(error.response?.data?.message || 'Failed to add plan', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const renderFeatureValue = (value) => {
        if (typeof value === 'boolean') {
            return value ? 
                <i className="ri-checkbox-circle-fill text-success fs-18"/> : 
                <i className="ri-close-circle-fill text-danger fs-18"/>;
        }
        if (value === 'Enabled') {
            return <Badge color="success">Enabled</Badge>;
        }
        if (value === 'Disabled') {
            return <Badge color="danger">Disabled</Badge>;
        }
        if (value === 'Hide') {
            return <Badge color="info">Hidden</Badge>;
        }
        return value;
    };

    const getColorScheme = (planSection) => {
        if (!planSection) return { bg: 'light', text: 'dark' };
        
        // Convert to uppercase for consistent matching
        const section = planSection.toUpperCase();
        
        switch (section) {
            case 'ESSENTIAL':
                return { bg: 'info', text: 'white' };
            case 'PREMIUM':
                return { bg: 'warning', text: 'dark' };
            case 'BLACK':
                return { bg: 'dark', text: 'white' };
            default:
                return { bg: 'light', text: 'dark' };
        }
    };

    // Helper to safely access objects and values
    const getDescription = (description) => {
        if (!description) return '';
        if (typeof description === 'string') return description;
        if (description.en) return description.en;
        return '';
    };

    // Helper to safely get values with defaults
    const getSafeValue = (obj, defaultValue = '') => {
        if (!obj) return defaultValue;
        if (obj.value !== undefined) return obj.value;
        return defaultValue;
    };
    
    // Helper to safely get objects
    const getSafeObject = (obj, defaultObj = {}) => {
        return obj || defaultObj;
    };

    const renderEditModal = () => (
        <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
            <ModalHeader toggle={() => setEditModal(false)}>
                Edit {getDescription(editForm?.planName)}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {editForm && (
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Plan Name</Label>
                                    <Input
                                        type="text"
                                        value={editForm.planName?.en || ''}
                                        onChange={e => handleFormChange('planName.en', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Description</Label>
                                    <Input
                                        type="text"
                                        value={editForm.description?.en || ''}
                                        onChange={e => handleFormChange('description.en', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Plan Section</Label>
                                    <Input
                                        type="select"
                                        value={editForm.planSection ? editForm.planSection.toUpperCase() : 'ESSENTIAL'}
                                        onChange={e => handleFormChange('planSection', e.target.value)}
                                    >
                                        <option value="ESSENTIAL">Essential</option>
                                        <option value="PREMIUM">Premium</option>
                                        <option value="BLACK">Black</option>
                                        <option value="OTHER">Other</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Amount</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={editForm.amount || 0}
                                        onChange={e => handleFormChange('amount', parseFloat(e.target.value))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Currency</Label>
                                    <Input
                                        type="select"
                                        value={editForm.currency?.currencyCode || 'USD'}
                                        onChange={e => {
                                            const currency = currencies.find(c => c.code === e.target.value);
                                            handleFormChange('currency', {
                                                currencyCode: currency.code,
                                                symbol: currency.symbol || currency.code
                                            });
                                        }}
                                    >
                                        {getUniqueCurrencies(currencies).map(currency => (
                                            <option 
                                                key={currency.code} 
                                                value={currency.code}
                                            >
                                                {currency.code} - {currency.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Duration</Label>
                                    <div className="d-flex gap-2">
                                        <Input
                                            type="number"
                                            value={editForm.duration || 1}
                                            onChange={e => handleFormChange('duration', parseInt(e.target.value))}
                                        />
                                        <Input
                                            type="select"
                                            value={editForm.durationType || 'MONTH'}
                                            onChange={e => handleFormChange('durationType', e.target.value)}
                                        >
                                            <option>MONTH</option>
                                            <option>YEAR</option>
                                        </Input>
                                    </div>
                                </FormGroup>
                            </Col>

                            {/* Features Section */}
                            <Col md={12}>
                                <h5 className="mt-4 mb-3">Features</h5>
                            </Col>
                            
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Tokens</Label>
                                    <Input
                                        type="number"
                                        value={getSafeValue(editForm.noOfTokensIncluded, 0)}
                                        onChange={e => handleFormChange('noOfTokensIncluded.value', parseInt(e.target.value))}
                                    />
                                    <Input
                                        type="text"
                                        className="mt-2"
                                        placeholder="Description (English)"
                                        value={getDescription(editForm.noOfTokensIncluded?.description) || ''}
                                        onChange={e => handleFormChange('noOfTokensIncluded.description.en', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Daily Likes</Label>
                                    <Input
                                        type="number"
                                        value={getSafeValue(editForm.dailyLikeCount, 0)}
                                        onChange={e => handleFormChange('dailyLikeCount.value', parseInt(e.target.value))}
                                    />
                                    <Input
                                        type="text"
                                        className="mt-2"
                                        placeholder="Description (English)"
                                        value={getDescription(editForm.dailyLikeCount?.description) || ''}
                                        onChange={e => handleFormChange('dailyLikeCount.description.en', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>

                            {/* Boolean Features */}
                            <Col md={12}>
                                <h6 className="mt-3">Access Controls</h6>
                                <div className="d-flex flex-wrap gap-4">
                                    {editForm.myList && Object.entries(editForm.myList).map(([key, value]) => {
                                        if (typeof value === 'boolean') {
                                            return (
                                                <FormGroup check key={key}>
                                                    <Label check>
                                                        <Input
                                                            type="checkbox"
                                                            checked={value}
                                                            onChange={e => handleFormChange(`myList.${key}`, e.target.checked)}
                                                        />
                                                        {key.replace(/([A-Z])/g, ' $1').replace(/Value/g, '').trim()}
                                                    </Label>
                                                </FormGroup>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </Col>

                            {/* Special Features */}
                            <Col md={12}>
                                <h6 className="mt-4">Special Features</h6>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Unlimited Rewinds</Label>
                                            <Input
                                                type="select"
                                                value={getSafeValue(editForm.allowUnlimitedRewinds, 'Disabled')}
                                                onChange={e => handleFormChange('allowUnlimitedRewinds.value', e.target.value)}
                                            >
                                                <option>Enabled</option>
                                                <option>Disabled</option>
                                            </Input>
                                            <Input
                                                type="text"
                                                className="mt-2"
                                                placeholder="Description (English)"
                                                value={getDescription(editForm.allowUnlimitedRewinds?.description) || ''}
                                                onChange={e => handleFormChange('allowUnlimitedRewinds.description.en', e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Passport Access</Label>
                                            <Input
                                                type="select"
                                                value={getSafeValue(editForm.passportToAnotherLocation, 'Disabled')}
                                                onChange={e => handleFormChange('passportToAnotherLocation.value', e.target.value)}
                                            >
                                                <option>Enabled</option>
                                                <option>Disabled</option>
                                            </Input>
                                            <Input
                                                type="text"
                                                className="mt-2"
                                                placeholder="Description (English)"
                                                value={getDescription(editForm.passportToAnotherLocation?.description) || ''}
                                                onChange={e => handleFormChange('passportToAnotherLocation.description.en', e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Profile Visibility Control</Label>
                                            <Input
                                                type="select"
                                                value={editForm.controlWhoSeesYourProfile?.value === true ? 'true' : 'false'}
                                                onChange={e => handleFormChange('controlWhoSeesYourProfile.value', e.target.value === 'true')}
                                            >
                                                <option value="true">Enabled</option>
                                                <option value="false">Disabled</option>
                                            </Input>
                                            <Input
                                                type="text"
                                                className="mt-2"
                                                placeholder="Description (English)"
                                                value={getDescription(editForm.controlWhoSeesYourProfile?.description) || ''}
                                                onChange={e => handleFormChange('controlWhoSeesYourProfile.description.en', e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Ads Visibility</Label>
                                            <Input
                                                type="select"
                                                value={getSafeValue(editForm.adsVisibility, 'Visible')}
                                                onChange={e => handleFormChange('adsVisibility.value', e.target.value)}
                                            >
                                                <option>Show</option>
                                                <option>Hide</option>
                                            </Input>
                                            <Input
                                                type="text"
                                                className="mt-2"
                                                placeholder="Description (English)"
                                                value={getDescription(editForm.adsVisibility?.description) || ''}
                                                onChange={e => handleFormChange('adsVisibility.description.en', e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>AI Matches</Label>
                                            <Input
                                                type="select"
                                                value={editForm.aiMatches?.value === true ? 'true' : 'false'}
                                                onChange={e => handleFormChange('aiMatches.value', e.target.value === 'true')}
                                            >
                                                <option value="true">Enabled</option>
                                                <option value="false">Disabled</option>
                                            </Input>
                                            <Input
                                                type="text"
                                                className="mt-2"
                                                placeholder="Description (English)"
                                                value={getDescription(editForm.aiMatches?.description) || ''}
                                                onChange={e => handleFormChange('aiMatches.description.en', e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Advanced Filter Access</Label>
                                            <Input
                                                type="select"
                                                value={editForm.advanceFilterAccess?.value === true ? 'true' : 'false'}
                                                onChange={e => handleFormChange('advanceFilterAccess.value', e.target.value === 'true')}
                                            >
                                                <option value="true">Enabled</option>
                                                <option value="false">Disabled</option>
                                            </Input>
                                            <Input
                                                type="text"
                                                className="mt-2"
                                                placeholder="Description (English)"
                                                value={getDescription(editForm.advanceFilterAccess?.description) || ''}
                                                onChange={e => handleFormChange('advanceFilterAccess.description.en', e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Status</Label>
                                            <Input
                                                type="select"
                                                value={editForm.status || 'ACTIVE'}
                                                onChange={e => handleFormChange('status', e.target.value)}
                                            >
                                                <option>ACTIVE</option>
                                                <option>INACTIVE</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>

                            {/* MyList Descriptions */}
                            <Col md={12}>
                                <h6 className="mt-4">MyList Feature Descriptions</h6>
                                <Row>
                                    {editForm.myList && Object.entries(editForm.myList).map(([key, value]) => {
                                        if (key.includes('Description')) {
                                            return (
                                                <Col md={6} key={key}>
                                                    <FormGroup>
                                                        <Label>{key.replace(/([A-Z])/g, ' $1').replace('Description', '').trim()}</Label>
                                                        <Input
                                                            type="text"
                                                            value={getDescription(value) || ''}
                                                            onChange={e => handleFormChange(`myList.${key}.en`, e.target.value)}
                                                            placeholder="Description (English)"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            );
                                        }
                                        return null;
                                    })}
                                </Row>
                            </Col>

                            {/* Store IDs */}
                            <Col md={6}>
                                <FormGroup>
                                    <Label>App Store ID</Label>
                                    <Input
                                        type="text"
                                        value={editForm.appStoreId || ''}
                                        onChange={e => handleFormChange('appStoreId', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Play Store ID</Label>
                                    <Input
                                        type="text"
                                        value={editForm.playStoreId || ''}
                                        onChange={e => handleFormChange('playStoreId', e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setEditModal(false)}>Cancel</Button>
                    <Button color="primary" type="submit">Save Changes</Button>
                </ModalFooter>
            </Form>
        </Modal>
    );

    const renderEditButton = (plan) => (
        <div className="d-flex gap-2">
                                <Button
                color="link"
                className="p-0 text-muted"
                onClick={() => fetchPlanDetails(plan._id)}
            >
                <i className="ri-pencil-line fs-16"/>
                                </Button>
                            <Button
                color="link"
                className="p-0 text-danger"
                onClick={() => handleInactivate(plan._id)}
            >
                <i className="ri-delete-bin-line fs-16"/>
                            </Button>
        </div>
    );

    // Add this function to filter and format currencies
    const getUniqueCurrencies = (currencies) => {
        const uniqueCurrencies = new Map();
        
        currencies.forEach(currency => {
            if (currency.code && !uniqueCurrencies.has(currency.code)) {
                uniqueCurrencies.set(currency.code, currency);
            }
        });

        return Array.from(uniqueCurrencies.values())
            .filter(currency => currency.code && currency.code !== '(none)' && currency.name)
            .sort((a, b) => a.code.localeCompare(b.code));
    };

    const renderAddModal = () => (
        <Modal isOpen={addModal} toggle={() => setAddModal(false)} size="lg">
            <ModalHeader toggle={() => setAddModal(false)}>
                Add New Plan
            </ModalHeader>
            <Form onSubmit={handleAddPlan}>
                <ModalBody>
                    <Row>
                        {/* Basic Details */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>Plan Name</Label>
                                <Input
                                    type="text"
                                    value={newPlan.planName.en}
                                    onChange={e => setNewPlan({...newPlan, planName: { en: e.target.value }})}
                                    placeholder="Plan Name (English)"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Description</Label>
                                <Input
                                    type="text"
                                    value={newPlan.description.en}
                                    onChange={e => setNewPlan({...newPlan, description: { en: e.target.value }})}
                                    placeholder="Description (English)"
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Plan Section</Label>
                                <Input
                                    type="select"
                                    value={newPlan.planSection}
                                    onChange={e => setNewPlan({...newPlan, planSection: e.target.value})}
                                    required
                                >
                                    <option value="ESSENTIAL">Essential</option>
                                    <option value="PREMIUM">Premium</option>
                                    <option value="BLACK">Black</option>
                                    <option value="OTHER">Other</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Amount</Label>
                                <Input
                                    type="number"
                                    value={newPlan.amount}
                                    onChange={e => setNewPlan({...newPlan, amount: parseFloat(e.target.value)})}
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Currency</Label>
                                <Input
                                    type="select"
                                    value={newPlan.currency.currencyCode}
                                    onChange={e => {
                                        const currency = currencies.find(c => c.code === e.target.value);
                                        setNewPlan({
                                            ...newPlan,
                                            currency: {
                                                currencyCode: currency.code,
                                                symbol: currency.symbol || currency.code
                                            }
                                        });
                                    }}
                                >
                                    {getUniqueCurrencies(currencies).map(currency => (
                                        <option key={currency.code} value={currency.code}>
                                            {currency.code} - {currency.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Duration</Label>
                                <div className="d-flex gap-2">
                                    <Input
                                        type="number"
                                        value={newPlan.duration}
                                        onChange={e => setNewPlan({...newPlan, duration: parseInt(e.target.value)})}
                                        required
                                    />
                                    <Input
                                        type="select"
                                        value={newPlan.durationType}
                                        onChange={e => setNewPlan({...newPlan, durationType: e.target.value})}
                                    >
                                        <option>MONTH</option>
                                        <option>YEAR</option>
                                    </Input>
                                </div>
                            </FormGroup>
                        </Col>

                        {/* Store IDs */}
                        <Col md={6}>
                            <FormGroup>
                                <Label>App Store ID</Label>
                                <Input
                                    type="text"
                                    value={newPlan.appStoreId}
                                    onChange={e => setNewPlan({...newPlan, appStoreId: e.target.value})}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Play Store ID</Label>
                                <Input
                                    type="text"
                                    value={newPlan.playStoreId}
                                    onChange={e => setNewPlan({...newPlan, playStoreId: e.target.value})}
                                />
                            </FormGroup>
                        </Col>

                        {/* Features */}
                        <Col md={12}>
                            <h6 className="mt-4">Features</h6>
                            <Row>
                                {/* Tokens and Daily Likes */}
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Tokens</Label>
                                        <Input
                                            type="number"
                                            value={newPlan.noOfTokensIncluded.value}
                                            onChange={e => setNewPlan({
                                                ...newPlan,
                                                noOfTokensIncluded: {
                                                    ...newPlan.noOfTokensIncluded,
                                                    value: parseInt(e.target.value)
                                                }
                                            })}
                                        />
                                        <Input
                                            type="text"
                                            className="mt-2"
                                            placeholder="Description (English)"
                                            value={newPlan.noOfTokensIncluded.description?.en}
                                            onChange={e => setNewPlan({
                                                ...newPlan,
                                                noOfTokensIncluded: {
                                                    ...newPlan.noOfTokensIncluded,
                                                    description: { en: e.target.value }
                                                }
                                            })}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Daily Likes</Label>
                                        <Input
                                            type="number"
                                            value={newPlan.dailyLikeCount.value}
                                            onChange={e => setNewPlan({
                                                ...newPlan,
                                                dailyLikeCount: {
                                                    ...newPlan.dailyLikeCount,
                                                    value: parseInt(e.target.value)
                                                }
                                            })}
                                        />
                                        <Input
                                            type="text"
                                            className="mt-2"
                                            placeholder="Description (English)"
                                            value={newPlan.dailyLikeCount.description?.en}
                                            onChange={e => setNewPlan({
                                                ...newPlan,
                                                dailyLikeCount: {
                                                    ...newPlan.dailyLikeCount,
                                                    description: { en: e.target.value }
                                                }
                                            })}
                                        />
                                    </FormGroup>
                                </Col>

                                {/* Access Controls */}
                                <Col md={12}>
                                    <h6 className="mt-3">Access Controls</h6>
                                    <div className="d-flex flex-wrap gap-4">
                                        {Object.entries(newPlan.myList).map(([key, value]) => {
                                            if (key.endsWith('Value')) {
                                                return (
                                                    <FormGroup check key={key}>
                                                        <Label check>
                                                            <Input
                                                                type="checkbox"
                                                                checked={value}
                                                                onChange={e => setNewPlan({
                                                                    ...newPlan,
                                                                    myList: {
                                                                        ...newPlan.myList,
                                                                        [key]: e.target.checked
                                                                    }
                                                                })}
                                                            />
                                                            {key.replace(/([A-Z])/g, ' $1').replace(/Value/g, '').trim()}
                                                        </Label>
                                                    </FormGroup>
                                                );
                                            }
                                            return null;
                                        })}
                                    </div>
                                </Col>

                                {/* Access Controls Descriptions */}
                                <Col md={12}>
                                    <h6 className="mt-4">Access Controls Descriptions</h6>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Recent Visitors Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Description (English)"
                                                    value={newPlan.myList.recentVisitorsDescription?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        myList: {
                                                            ...newPlan.myList,
                                                            recentVisitorsDescription: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>My Matches Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Description (English)"
                                                    value={newPlan.myList.myMatchesDescription?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        myList: {
                                                            ...newPlan.myList,
                                                            myMatchesDescription: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Maybe Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Description (English)"
                                                    value={newPlan.myList.maybeDescription?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        myList: {
                                                            ...newPlan.myList,
                                                            maybeDescription: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>My Likes Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Description (English)"
                                                    value={newPlan.myList.myLikesDescription?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        myList: {
                                                            ...newPlan.myList,
                                                            myLikesDescription: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Likes Me Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Description (English)"
                                                    value={newPlan.myList.likesMeDescription?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        myList: {
                                                            ...newPlan.myList,
                                                            likesMeDescription: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Liked Hooks Description</Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Description (English)"
                                                    value={newPlan.myList.likedHooksDescription?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        myList: {
                                                            ...newPlan.myList,
                                                            likedHooksDescription: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Col>

                                {/* Special Features */}
                                <Col md={12}>
                                    <h6 className="mt-4">Special Features</h6>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Unlimited Rewinds</Label>
                                                <Input
                                                    type="select"
                                                    value={newPlan.allowUnlimitedRewinds.value}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        allowUnlimitedRewinds: {
                                                            ...newPlan.allowUnlimitedRewinds,
                                                            value: e.target.value
                                                        }
                                                    })}
                                                >
                                                    <option>Enabled</option>
                                                    <option>Disabled</option>
                                                </Input>
                                                <Input
                                                    type="text"
                                                    className="mt-2"
                                                    placeholder="Description (English)"
                                                    value={newPlan.allowUnlimitedRewinds.description?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        allowUnlimitedRewinds: {
                                                            ...newPlan.allowUnlimitedRewinds,
                                                            description: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Passport Access</Label>
                                                <Input
                                                    type="select"
                                                    value={newPlan.passportToAnotherLocation.value}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        passportToAnotherLocation: {
                                                            ...newPlan.passportToAnotherLocation,
                                                            value: e.target.value
                                                        }
                                                    })}
                                                >
                                                    <option>Enabled</option>
                                                    <option>Disabled</option>
                                                </Input>
                                                <Input
                                                    type="text"
                                                    className="mt-2"
                                                    placeholder="Description (English)"
                                                    value={newPlan.passportToAnotherLocation.description?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        passportToAnotherLocation: {
                                                            ...newPlan.passportToAnotherLocation,
                                                            description: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>AI Matches</Label>
                                                <Input
                                                    type="select"
                                                    value={newPlan.aiMatches.value}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        aiMatches: {
                                                            ...newPlan.aiMatches,
                                                            value: e.target.value === 'true'
                                                        }
                                                    })}
                                                >
                                                    <option value="true">Enabled</option>
                                                    <option value="false">Disabled</option>
                                                </Input>
                                                <Input
                                                    type="text"
                                                    className="mt-2"
                                                    placeholder="Description (English)"
                                                    value={newPlan.aiMatches.description?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        aiMatches: {
                                                            ...newPlan.aiMatches,
                                                            description: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Ads Visibility</Label>
                                                <Input
                                                    type="select"
                                                    value={newPlan.adsVisibility.value}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        adsVisibility: {
                                                            ...newPlan.adsVisibility,
                                                            value: e.target.value
                                                        }
                                                    })}
                                                >
                                                    <option>Show</option>
                                                    <option>Hide</option>
                                                </Input>
                                                <Input
                                                    type="text"
                                                    className="mt-2"
                                                    placeholder="Description (English)"
                                                    value={newPlan.adsVisibility.description?.en || ''}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        adsVisibility: {
                                                            ...newPlan.adsVisibility,
                                                            description: { en: e.target.value }
                                                        }
                                                    })}
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Profile Visibility Control</Label>
                                                <Input
                                                    type="select"
                                                    value={newPlan.controlWhoSeesYourProfile.value}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        controlWhoSeesYourProfile: {
                                                            ...newPlan.controlWhoSeesYourProfile,
                                                            value: e.target.value === 'true'
                                                        }
                                                    })}
                                                >
                                                    <option value="true">Enabled</option>
                                                    <option value="false">Disabled</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label>Advanced Filter Access</Label>
                                                <Input
                                                    type="select"
                                                    value={newPlan.advanceFilterAccess.value}
                                                    onChange={e => setNewPlan({
                                                        ...newPlan,
                                                        advanceFilterAccess: {
                                                            ...newPlan.advanceFilterAccess,
                                                            value: e.target.value === 'true'
                                                        }
                                                    })}
                                                >
                                                    <option value="true">Enabled</option>
                                                    <option value="false">Disabled</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setAddModal(false)}>Cancel</Button>
                    <Button color="success" type="submit">Create Plan</Button>
                </ModalFooter>
            </Form>
        </Modal>
    );

    const renderTabs = () => {
        return (
            <Nav tabs className="nav-tabs-custom mb-3">
                <NavItem>
                    <NavLink
                        className={activeTab === 'active' ? 'active' : ''}
                        onClick={() => setActiveTab('active')}
                        style={{ cursor: 'pointer' }}
                    >
                        Active Plans
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={activeTab === 'inactive' ? 'active' : ''}
                        onClick={() => setActiveTab('inactive')}
                        style={{ cursor: 'pointer' }}
                    >
                        Inactive Plans
                    </NavLink>
                </NavItem>
            </Nav>
        );
    };

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <Container fluid>
                <BreadCrumb title="Subscription Plans" pageTitle="Plans" />
                
                {renderTabs()}

                <TabContent activeTab={activeTab}>
                    <TabPane tabId="active">
                        <Row className="mb-4">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="search-box">
                                                <Input 
                                            type="text"
                                                    className="form-control"
                                            placeholder="Search plans..."
                                            value={searchText}
                                            onChange={(e) => {
                                                setSearchText(e.target.value);
                                                clearTimeout(window.searchTimeout);
                                                window.searchTimeout = setTimeout(() => {
                                                    fetchData();
                                                }, 500);
                                            }}
                                        />
                                        <i className="ri-search-line search-icon"></i>
                                            </div>
                                    <Button color="success" onClick={() => setAddModal(true)}>
                                        <i className="ri-add-line align-bottom me-1"></i> Add Plan
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center">
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            Object.entries(groupedPlans).map(([planSection, variants]) => {
                                const { bg, text } = getColorScheme(planSection);
                                // Format the section title
                                const sectionTitle = planSection === 'OTHER' ? 'Other Plans' : 
                                  `Koottu ${planSection.charAt(0) + planSection.slice(1).toLowerCase()}`;
                                
                                return (
                                    <Card key={planSection} className="mb-4">
                                        <CardHeader className={`bg-${bg} text-${text}`}>
                                            <h4 className="card-title mb-0">{sectionTitle}</h4>
                                        </CardHeader>
                                        <CardBody>
                                            <Row>
                                                {variants.map(plan => (
                                                    <Col md={4} key={plan._id}>
                                                        <Card className="border">
                                                            <CardBody>
                                                                <div className="text-center mb-4">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <h5 className="fw-semibold">{getDescription(plan.description)}</h5>
                                                                        {renderEditButton(plan)}
                                                                    </div>
                                                                    <div className="fs-1 fw-bold">
                                                                        {plan.currency.symbol}{plan.amount}
                                                                    </div>
                                                                    <div className="text-muted">
                                                                        per {plan.duration} {plan.durationType.toLowerCase()}
                                                                    </div>
                                                                </div>

                                                                <div className="plan-features">
                                                                    <div className="d-flex align-items-center mb-3">
                                                                        <i className="ri-coin-line text-warning me-2 fs-18"/>
                                                                        <div>
                                                                            <strong>{plan.noOfTokensIncluded.value}</strong> {getDescription(plan.noOfTokensIncluded.description)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center mb-3">
                                                                        <i className="ri-heart-line text-danger me-2 fs-18"/>
                                                                        <div>
                                                                            <strong>{plan.dailyLikeCount.value}</strong> {getDescription(plan.dailyLikeCount.description)}
                                                                        </div>
                                                                    </div>

                                                <Table className="table-borderless mb-0">
                                                    <tbody>
                                                        <tr>
                                                                                <td>Recent Visitors</td>
                                                                                <td>{renderFeatureValue(plan.myList.recentVisitorsValue)}</td>
                                                        </tr>
                                                        <tr>
                                                                                <td>Unlimited Rewinds</td>
                                                                                <td>{renderFeatureValue(plan.allowUnlimitedRewinds.value)}</td>
                                                        </tr>
                                                        <tr>
                                                                                <td>Passport Access</td>
                                                                                <td>{renderFeatureValue(plan.passportToAnotherLocation.value)}</td>
                                                        </tr>
                                                        <tr>
                                                                                <td>Advanced Filters</td>
                                                                                <td>{renderFeatureValue(plan.advanceFilterAccess.value)}</td>
                                                        </tr>
                                                        <tr>
                                                                                <td>AI Matches</td>
                                                                                <td>{renderFeatureValue(plan.aiMatches.value)}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>

                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <small className="text-muted">
                                                                            Store IDs: <br/>
                                                                            App Store: {plan.appStoreId}<br/>
                                                                            Play Store: {plan.playStoreId}
                                                                        </small>
                                            </div>
                                        </div>
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </CardBody>
                                    </Card>
                                );
                            })
                        )}
                    </TabPane>

                    <TabPane tabId="inactive">
                        <Row className="mb-4">
                            <Col>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="search-box">
                                        <Input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search inactive plans..."
                                            value={searchText}
                                            onChange={(e) => {
                                                setSearchText(e.target.value);
                                                clearTimeout(window.searchTimeout);
                                                window.searchTimeout = setTimeout(() => {
                                                    fetchData();
                                                }, 500);
                                            }}
                                        />
                                        <i className="ri-search-line search-icon"></i>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center">
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            Object.entries(groupedPlans).map(([planSection, variants]) => {
                                const { bg, text } = getColorScheme(planSection);
                                // Format the section title
                                const sectionTitle = planSection === 'OTHER' ? 'Other Plans' : 
                                  `Koottu ${planSection.charAt(0) + planSection.slice(1).toLowerCase()}`;
                                
                                return (
                                    <Card key={planSection} className="mb-4">
                                        <CardHeader className={`bg-${bg} text-${text}`}>
                                            <h4 className="card-title mb-0">{sectionTitle}</h4>
                                        </CardHeader>
                                        <CardBody>
                                            <Row>
                                                {variants.map(plan => (
                                                    <Col md={4} key={plan._id}>
                                                        <Card className="border">
                                                            <CardBody>
                                                                <div className="text-center mb-4">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <h5 className="fw-semibold">{getDescription(plan.description)}</h5>
                                                                        {renderEditButton(plan)}
                                                                    </div>
                                                                    <div className="fs-1 fw-bold">
                                                                        {plan.currency.symbol}{plan.amount}
                                                                    </div>
                                                                    <div className="text-muted">
                                                                        per {plan.duration} {plan.durationType.toLowerCase()}
                                                                    </div>
                                                                </div>

                                                                <div className="plan-features">
                                                                    <div className="d-flex align-items-center mb-3">
                                                                        <i className="ri-coin-line text-warning me-2 fs-18"/>
                                                                        <div>
                                                                            <strong>{plan.noOfTokensIncluded.value}</strong> {getDescription(plan.noOfTokensIncluded.description)}
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex align-items-center mb-3">
                                                                        <i className="ri-heart-line text-danger me-2 fs-18"/>
                                                                        <div>
                                                                            <strong>{plan.dailyLikeCount.value}</strong> {getDescription(plan.dailyLikeCount.description)}
                                                                        </div>
                                                                    </div>

                                                                    <Table className="table-borderless mb-0">
                                                                        <tbody>
                                                                            <tr>
                                                                                <td>Recent Visitors</td>
                                                                                <td>{renderFeatureValue(plan.myList.recentVisitorsValue)}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Unlimited Rewinds</td>
                                                                                <td>{renderFeatureValue(plan.allowUnlimitedRewinds.value)}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Passport Access</td>
                                                                                <td>{renderFeatureValue(plan.passportToAnotherLocation.value)}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>Advanced Filters</td>
                                                                                <td>{renderFeatureValue(plan.advanceFilterAccess.value)}</td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td>AI Matches</td>
                                                                                <td>{renderFeatureValue(plan.aiMatches.value)}</td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </Table>

                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <small className="text-muted">
                                                                            Store IDs: <br/>
                                                                            App Store: {plan.appStoreId}<br/>
                                                                            Play Store: {plan.playStoreId}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </CardBody>
                                    </Card>
                                );
                            })
                        )}
                    </TabPane>
                </TabContent>
            </Container>
            {renderEditModal()}
            {renderAddModal()}
            {showDeleteAlert && (
                <Swal
                    title="Are you sure?"
                    text="This plan will be marked as inactive!"
                    type="warning"
                    showCancelButton
                    confirmButtonText="Yes, inactivate it!"
                    cancelButtonText="No, cancel!"
                    onConfirm={handleConfirmDelete}
                    onCancel={() => {
                        setShowDeleteAlert(false);
                        setPlanToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

export default SubscriptionPlans;